/**
 * Main App Layout Component
 */

import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSite } from '../../hooks/useSite';
import {
  LayoutDashboard,
  Link2,
  Settings,
  LogOut,
  ChevronDown,
  ChevronLeft,
  Building2,
  Globe,
  Menu,
  X,
  Check,
  Network,
  AlertTriangle,
  Target,
  FileEdit,
  Code,
  Plus,
  Users,
  HelpCircle,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import vizzexLogo from '../../assets/VIZZEX.250.png';
import { sitesApi, authApi, type Site } from '../../lib/api';
import { useVersionCheck } from '../../hooks/useVersionCheck';

export function AppLayout() {
  const { user, organizations, currentOrganizationId, setCurrentOrganization, currentSite, setCurrentSite, logout } = useAuth();
  const { licenseStatus } = useSite();
  const location = useLocation();
  const { updateAvailable } = useVersionCheck();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [siteMenuOpen, setSiteMenuOpen] = useState(false);
  const [addSiteMode, setAddSiteMode] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  type SiteEntry = { site: Site; orgId: number; orgName: string };
  const [allSites, setAllSites] = useState<SiteEntry[]>([]);
  const navigate = useNavigate();

  // Derive the active org — prefer currentOrganizationId from session, fall back to first org
  const activeOrgId = currentOrganizationId ?? organizations[0]?.id ?? null;
  const activeOrg = organizations.find(o => o.id === activeOrgId) ?? organizations[0] ?? null;

  // Fetch sites from ALL organizations so the header dropdown spans orgs
  useEffect(() => {
    if (organizations.length === 0) return;
    const fetchAll = async () => {
      const results = await Promise.all(
        organizations.map(async (org) => {
          try {
            const res = await sitesApi.getByOrganization(org.id);
            if (res.success && res.sites) {
              return res.sites.map((site: Site) => ({ site, orgId: org.id, orgName: org.name }));
            }
          } catch { /* ignore per-org errors */ }
          return [];
        })
      );
      setAllSites(results.flat());
    };
    fetchAll();
  }, [organizations]);

  const isHtmlClient = currentSite?.cmsType === 'html' || currentSite?.platform === 'html';

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Link Recommendations', href: '/links', icon: Link2 },
    // Draft Post Links and Semantic Schema are not available for HTML/No-CMS clients
    ...(!isHtmlClient ? [{ name: 'Draft Post Links', href: '/draft-links', icon: FileEdit, badge: 'PRO' }] : []),
    { name: 'Content Analysis', href: '/content-analysis', icon: Network },
    { name: 'Posts Requiring Attention', href: '/posts-attention', icon: AlertTriangle },
    { name: 'Content Gaps', href: '/content-gaps', icon: Target },
    ...(!isHtmlClient ? [{ name: 'Semantic Schema', href: '/schema', icon: Code, badge: 'PRO' }] : []),
    // HTML/No-CMS clients get a review-only FAQ page — the full Semantic Schema section
    // (entity settings, signal architect, page mapping, post schema status) doesn't apply
    // since there's no CMS to inject markup into.
    ...(isHtmlClient ? [{ name: 'Post FAQs', href: '/post-faqs', icon: HelpCircle }] : []),
    ...(isHtmlClient ? [{ name: 'Team Members', href: '/team', icon: Users }] : []),
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const isActive = (href: string) => location.pathname === href;

  // Get license tier badge color
  const getLicenseBadgeClass = (tier: string | undefined) => {
    if (!tier) return 'bg-gray-500/20 text-gray-400';
    const tierLower = tier.toLowerCase();
    if (tierLower.includes('pro') || tierLower.includes('agency')) {
      return 'bg-[#ffdc18]/20 text-[#ffdc18]';
    }
    if (tierLower === 'free' || tierLower === 'basic') {
      return 'bg-gray-500/20 text-gray-400';
    }
    return 'bg-blue-500/20 text-blue-400';
  };

  // Get license display name - prefer SKU if available, otherwise use tier
  const getLicenseDisplayName = (sku: string | undefined, tier: string | undefined) => {
    // If we have a SKU, format it nicely
    if (sku) {
      // Format SKU: "PRO_MULTISITE" -> "PRO MULTISITE", "BASIC" -> "BASIC"
      return sku.toUpperCase().replace(/_/g, ' ');
    }
    // Fallback to tier name
    if (!tier) return 'FREE';
    return tier.toUpperCase().replace(/_/g, ' ');
  };

  // Legacy function for backwards compatibility with site.licenseTier
  const getLicenseTierName = (tier: string | undefined) => {
    if (!tier) return 'FREE';
    return tier.toUpperCase().replace(/_/g, ' ');
  };

  // Handle site switch
  const closeSiteMenu = () => {
    setSiteMenuOpen(false);
    setAddSiteMode(false);
  };

  const handleSiteSwitch = (entry: SiteEntry) => {
    if (entry.orgId !== activeOrgId) setCurrentOrganization(entry.orgId);
    setCurrentSite(entry.site);
    setSiteMenuOpen(false);
    // Persist the selection to the backend session so reloads and refreshUser()
    // calls continue to restore the correct site instead of defaulting back to
    // whatever was stored in the session previously.
    sitesApi.switch(entry.site.id).catch(() => {/* best-effort */});
    navigate('/dashboard');
  };

  return (
    <div className={`min-h-screen flex ${updateAvailable ? 'pt-9' : ''}`}>
      {/* New-version reload banner — fixed full-width bar above everything */}
      {updateAvailable && (
        <div
          className="fixed top-0 left-0 right-0 z-[100] flex items-center justify-center gap-4 px-4 py-2 text-black text-sm font-medium"
          style={{ backgroundColor: 'var(--vizzex-yellow, #ffdc18)' }}
        >
          <span>A new version of VizzEx is available.</span>
          <button
            onClick={() => window.location.reload()}
            className="px-3 py-1 rounded bg-black/15 hover:bg-black/25 transition-colors font-semibold text-xs"
          >
            Reload now
          </button>
        </div>
      )}

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out
          lg:relative lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          bg-[var(--bg-secondary)] border-r border-[var(--bg-tertiary)]
        `}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-[var(--bg-tertiary)]">
            <Link to="/dashboard" className="flex items-center">
              <img src={vizzexLogo} alt="VizzEx" className="h-8" />
            </Link>
            <button
              className="lg:hidden p-1 rounded hover:bg-[var(--bg-tertiary)]"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>


          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                    transition-all duration-200
                    ${active
                      ? 'bg-[#ffdc18]/20 text-[#ffdc18] border border-[#ffdc18]/30'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[#ffdc18]'
                    }
                  `}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1">{item.name}</span>
                  {'badge' in item && item.badge && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#ffdc18]/20 text-[#ffdc18]">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Organization */}
          {activeOrg && (
            <div className="px-3 py-3 border-t border-[var(--bg-tertiary)] relative">
              <button 
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-left hover:bg-[var(--bg-tertiary)] transition-colors"
                onClick={() => setOrgMenuOpen(!orgMenuOpen)}
              >
                <Building2 className="w-5 h-5 text-white/70" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{activeOrg.name}</p>
                  <p className="text-xs text-white/70 capitalize">{activeOrg.role}</p>
                </div>
                <ChevronDown className={`w-4 h-4 text-white/70 transition-transform ${orgMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Organization dropdown */}
              {orgMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setOrgMenuOpen(false)}
                  />
                  <div className="absolute bottom-full left-3 right-3 mb-2 py-1 bg-[var(--bg-secondary)] border border-[var(--bg-tertiary)] rounded-lg shadow-xl z-20">
                    <div className="px-3 py-2 border-b border-[var(--bg-tertiary)]">
                      <p className="text-xs text-white/70 font-medium uppercase">Organization</p>
                      <p className="font-medium truncate">{activeOrg.name}</p>
                    </div>
                    <Link
                      to="/settings"
                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--bg-tertiary)]"
                      onClick={() => {
                        setOrgMenuOpen(false);
                        setSidebarOpen(false);
                      }}
                    >
                      <Settings className="w-4 h-4" />
                      Organization Settings
                    </Link>
                    {activeOrg.role === 'owner' && (
                      <Link
                        to="/settings"
                        className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--bg-tertiary)]"
                        onClick={() => {
                          setOrgMenuOpen(false);
                          setSidebarOpen(false);
                        }}
                      >
                        <Building2 className="w-4 h-4" />
                        Manage Team
                      </Link>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="h-16 flex items-center justify-between px-4 lg:px-6 border-b border-[var(--bg-tertiary)] bg-[var(--bg-secondary)]/50 backdrop-blur-sm relative z-30">
          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-[var(--bg-tertiary)]"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Site Display with License Badge */}
          <div className="flex items-center gap-4 flex-1 justify-center lg:justify-start lg:pl-4">
            <div className="relative">
              {/* Show dropdown whenever there are multiple sites across all orgs, or multiple orgs */}
              {allSites.length <= 1 && organizations.length <= 1 ? (
                <div className="flex items-center gap-2 px-3 py-2">
                  <Globe className="w-4 h-4 text-white/70" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium truncate max-w-[250px]">
                      {currentSite?.siteName || 'No Site'}
                    </span>
                    {currentSite?.siteUrl && (
                      <span className="text-xs text-white/70 truncate max-w-[250px]">
                        {currentSite.siteUrl.replace(/^https?:\/\//, '')}
                      </span>
                    )}
                  </div>
                  {(licenseStatus?.productSku || licenseStatus?.tier) && (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getLicenseBadgeClass(licenseStatus.tier)}`}>
                      {getLicenseDisplayName(licenseStatus.productSku, licenseStatus.tier)}
                    </span>
                  )}
                </div>
              ) : (
                <>
                  <button
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
                    onClick={() => { if (siteMenuOpen) { closeSiteMenu(); } else { setSiteMenuOpen(true); } }}
                  >
                    <Globe className="w-4 h-4 text-white/70" />
                    <span className="text-sm font-medium truncate max-w-[200px]">
                      {currentSite?.siteName || 'Select Site'}
                    </span>
                    {(licenseStatus?.productSku || licenseStatus?.tier) && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getLicenseBadgeClass(licenseStatus.tier)}`}>
                        {getLicenseDisplayName(licenseStatus.productSku, licenseStatus.tier)}
                      </span>
                    )}
                    <ChevronDown className="w-4 h-4 text-white/70" />
                  </button>

                  {siteMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={closeSiteMenu} />
                      <div className="absolute left-0 mt-2 w-72 py-1 bg-[var(--bg-secondary)] border border-[var(--bg-tertiary)] rounded-lg shadow-xl z-50">

                        {addSiteMode ? (
                          <>
                            <div className="px-3 py-2 border-b border-[var(--bg-tertiary)] flex items-center gap-2">
                              <button
                                className="p-0.5 rounded hover:bg-[var(--bg-tertiary)] text-white/50 hover:text-white transition-colors"
                                onClick={() => setAddSiteMode(false)}
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </button>
                              <p className="text-xs text-white/70 font-medium">ADD NEW SITE</p>
                            </div>
                            <button
                              className="w-full flex items-start gap-3 px-4 py-3 text-sm text-left hover:bg-[var(--bg-tertiary)] transition-colors"
                              onClick={() => { closeSiteMenu(); window.location.href = authApi.getHubSpotOAuthUrl(); }}
                            >
                              <div className="w-8 h-8 rounded-lg bg-[#ff7a59]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-[#ff7a59] font-bold text-xs">HS</span>
                              </div>
                              <div>
                                <p className="font-medium">Connect with HubSpot</p>
                                <p className="text-xs text-white/50 mt-0.5">OAuth — links directly to your HubSpot portal</p>
                              </div>
                            </button>
                            <button
                              className="w-full flex items-start gap-3 px-4 py-3 text-sm text-left hover:bg-[var(--bg-tertiary)] transition-colors"
                              onClick={() => { closeSiteMenu(); navigate('/connect-html-site'); }}
                            >
                              <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Globe className="w-4 h-4 text-blue-400" />
                              </div>
                              <div>
                                <p className="font-medium">HTML / No CMS</p>
                                <p className="text-xs text-white/50 mt-0.5">Connect any blog by URL — WordPress, Webflow, Ghost…</p>
                              </div>
                            </button>
                          </>
                        ) : (
                          <>
                            {/* Group sites by org when user belongs to multiple orgs */}
                            {organizations.length > 1 ? (
                              organizations.map(org => {
                                const orgSites = allSites.filter(e => e.orgId === org.id);
                                if (orgSites.length === 0) return null;
                                return (
                                  <div key={org.id}>
                                    <div className="px-3 py-1.5 border-b border-[var(--bg-tertiary)]">
                                      <p className="text-[10px] text-white/40 font-semibold uppercase tracking-wider">{org.name}</p>
                                    </div>
                                    {orgSites.map(entry => (
                                      <button
                                        key={entry.site.id}
                                        className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-[var(--bg-tertiary)] ${
                                          currentSite?.id === entry.site.id ? 'bg-[var(--bg-tertiary)]' : ''
                                        }`}
                                        onClick={() => handleSiteSwitch(entry)}
                                      >
                                        <Globe className="w-4 h-4 text-white/70" />
                                        <div className="flex-1 min-w-0">
                                          <p className="font-medium truncate">{entry.site.siteName}</p>
                                          <p className="text-xs text-white/70 truncate">{entry.site.siteUrl}</p>
                                        </div>
                                        {currentSite?.id === entry.site.id && (
                                          <Check className="w-4 h-4 text-[#ffdc18]" />
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                );
                              })
                            ) : (
                              <>
                                <div className="px-3 py-2 border-b border-[var(--bg-tertiary)]">
                                  <p className="text-xs text-white/70 font-medium">SWITCH SITE</p>
                                </div>
                                {allSites.map(entry => (
                                  <button
                                    key={entry.site.id}
                                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-[var(--bg-tertiary)] ${
                                      currentSite?.id === entry.site.id ? 'bg-[var(--bg-tertiary)]' : ''
                                    }`}
                                    onClick={() => handleSiteSwitch(entry)}
                                  >
                                    <Globe className="w-4 h-4 text-white/70" />
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium truncate">{entry.site.siteName}</p>
                                      <p className="text-xs text-white/70 truncate">{entry.site.siteUrl}</p>
                                    </div>
                                    {entry.site.licenseTier && (
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getLicenseBadgeClass(entry.site.licenseTier)}`}>
                                        {getLicenseTierName(entry.site.licenseTier)}
                                      </span>
                                    )}
                                    {currentSite?.id === entry.site.id && (
                                      <Check className="w-4 h-4 text-[#ffdc18]" />
                                    )}
                                  </button>
                                ))}
                              </>
                            )}
                            <div className="border-t border-[var(--bg-tertiary)] mt-1 pt-1">
                              <button
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--color-primary-400)] hover:bg-[var(--bg-tertiary)] transition-colors"
                                onClick={() => setAddSiteMode(true)}
                              >
                                <Plus className="w-4 h-4" />
                                Add New Site
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* User menu */}
          <div className="relative">
            <button
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <div className="w-8 h-8 rounded-full bg-[#ffdc18] flex items-center justify-center">
                <span className="text-gray-900 font-medium text-sm">
                  {user?.name?.[0] || user?.email?.[0]?.toUpperCase() || '?'}
                </span>
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium">{user?.name || 'User'}</p>
                <p className="text-xs text-white/70">{user?.email}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-white/70" />
            </button>

            {/* Dropdown */}
            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setUserMenuOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-48 py-1 bg-[var(--bg-secondary)] border border-[var(--bg-tertiary)] rounded-lg shadow-xl z-50">
                  <Link
                    to="/settings"
                    className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-[var(--bg-tertiary)]"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </Link>
                  <button
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[var(--color-error)] hover:bg-[var(--bg-tertiary)]"
                    onClick={() => {
                      setUserMenuOpen(false);
                      logout();
                    }}
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default AppLayout;
